import React from 'react';
import style from '../../assets/styles/RequestListToBePrepared.module.scss';

// Extrai o número da mesa a partir do pedido
export const getTableNumber = (order) => {
  if (
    order?.tableNumber !== undefined &&
    order?.tableNumber !== null &&
    String(order.tableNumber).trim() !== ''
  ) {
    return String(order.tableNumber).trim();
  }
  if (
    order?.mesa !== undefined &&
    order?.mesa !== null &&
    String(order.mesa).trim() !== ''
  ) {
    return String(order.mesa).trim();
  }
  return null;
};

// Cálculo de status e cor para as mesas no painel superior do PDV
export const getTableStatusStyle = (ordersForTable) => {
  // 1. Azul piscando: Cliente chamando o garçom (URGENTE)
  const hasWaiterCall = ordersForTable.some(
    (o) => o.waiterCall && o.waiterCall.active === true
  );
  if (hasWaiterCall) {
    return {
      className: `${style.tableBlue} ${style.tableBlueBlinking}`,
      label: 'Chamou o garçom',
    };
  }

  // 2. Verde piscando: Cliente quer a conta (chamado ativo para o garçom)
  const hasActiveBillCall = ordersForTable.some(
    (o) => o.paymentCall && o.paymentCall.active === true
  );
  if (hasActiveBillCall) {
    return {
      className: `${style.tableGreen} ${style.tableGreenBlinking}`,
      label: 'Pediu a conta',
    };
  }

  // 2.1. Verde sólido: Garçom já atendeu o chamado, aguardando pagamento
  const hasPendingBill = ordersForTable.some(
    (o) => Boolean(o.paymentCall) && (!o.paymentDone || !o.orderDelivered)
  );
  if (hasPendingBill) {
    return { className: style.tableGreen, label: 'Conta solicitada' };
  }

  // 3. Laranja: Prato pronto para ser enviado para a mesa (pronto && !entregue)
  const hasDishReady = ordersForTable.some(
    (o) =>
      o.request &&
      Array.isArray(o.request) &&
      o.request.some((req) => req.pronto && !req.entregue)
  );
  if (hasDishReady) {
    return { className: style.tableOrange, label: 'Prato pronto para entrega' };
  }

  // 3. Vermelha: Acabaram de fazer pedido / pratos em preparo (!pronto && !entregue)
  const hasPendingPrep = ordersForTable.some(
    (o) =>
      o.request &&
      Array.isArray(o.request) &&
      o.request.some((req) => !req.pronto && !req.entregue)
  );
  const hasNoItemsYet = ordersForTable.some(
    (o) => !o.orderDelivered && (!o.request || o.request.length === 0)
  );
  if (hasPendingPrep || hasNoItemsYet) {
    return { className: style.tableRed, label: 'Pedido recém feito / em preparo' };
  }

  // 4. Sem preenchimento: Todos os pedidos já entregues na mesa
  return { className: style.tableDelivered, label: 'Todos os pratos entregues' };
};

// Agrupa e ordena os pedidos exibidos no PDV de acordo com filtro selecionado
export const getPdvDisplayedElements = (requestsDoneList, selectedTableFilter) => {
  if (!requestsDoneList || requestsDoneList.length === 0) return [];

  // Contar pedidos por mesa para agrupamento multi-cliente
  const tableCounts = {};
  requestsDoneList.forEach((order) => {
    const table = getTableNumber(order);
    if (table) {
      tableCounts[table] = (tableCounts[table] || 0) + 1;
    }
  });

  // Construir lista de grupos e pedidos individuais
  const elements = [];
  const processedTables = new Set();

  requestsDoneList.forEach((order) => {
    const table = getTableNumber(order);
    const isMultiOrderTable = table && tableCounts[table] > 1;

    if (isMultiOrderTable) {
      if (!processedTables.has(table)) {
        processedTables.add(table);
        const tableOrders = requestsDoneList.filter(
          (o) => getTableNumber(o) === table
        );
        elements.push({
          type: 'tableGroup',
          tableNumber: table,
          orders: tableOrders,
          key: `table-group-${table}`,
        });
      }
    } else {
      elements.push({
        type: 'singleOrder',
        order,
        key: order.id,
      });
    }
  });

  // Ordenar pilha de pedidos da mesa menor para a maior
  elements.sort((a, b) => {
    const tableA = a.type === 'tableGroup' ? a.tableNumber : getTableNumber(a.order);
    const tableB = b.type === 'tableGroup' ? b.tableNumber : getTableNumber(b.order);

    if (tableA && tableB) {
      const numA = parseInt(tableA, 10);
      const numB = parseInt(tableB, 10);
      if (!isNaN(numA) && !isNaN(numB)) {
        return numA - numB;
      }
      return String(tableA).localeCompare(String(tableB), undefined, { numeric: true });
    }

    // Mesas vêm antes de pedidos pré-pagos
    if (tableA && !tableB) return -1;
    if (!tableA && tableB) return 1;

    // Ambos pré-pagos: ordenar por countRequest crescente
    const countA = a.order?.countRequest || 0;
    const countB = b.order?.countRequest || 0;
    return countA - countB;
  });

  // Filtrar por mesa ou pedido pré-pago selecionado
  return elements.filter((element) => {
    if (!selectedTableFilter) return true;

    if (selectedTableFilter.startsWith('prepago-')) {
      const prepagoId = selectedTableFilter.replace('prepago-', '');
      return element.type === 'singleOrder' && element.order?.id === prepagoId;
    }

    if (element.type === 'tableGroup') {
      return element.tableNumber === selectedTableFilter;
    }

    return getTableNumber(element.order) === selectedTableFilter;
  });
};

/**
 * Painel Superior do PDV: Botões de Mesas Ocupadas, Pedidos Pré-Pagos e Barra de Filtro
 */
const PdvTablesDashboard = ({
  requestsDoneList = [],
  selectedTableFilter = null,
  onSelectTable,
  onClearFilter,
}) => {
  // Extrair mesas ativas (ocupadas) e pedidos pré-pagos
  const { occupiedTables, tableOrdersMap, prePagoOrders } = React.useMemo(() => {
    const occupied = [];
    const map = new Map();
    const prepago = [];

    requestsDoneList.forEach((order) => {
      const table = getTableNumber(order);
      if (table) {
        if (!map.has(table)) {
          map.set(table, []);
          occupied.push(table);
        }
        map.get(table).push(order);
      } else {
        prepago.push(order);
      }
    });

    // Ordenar mesas numericamente crescente (ex: 01, 13, 15, 17...)
    occupied.sort((a, b) => {
      const numA = parseInt(a, 10);
      const numB = parseInt(b, 10);
      if (!isNaN(numA) && !isNaN(numB)) {
        return numA - numB;
      }
      return String(a).localeCompare(String(b), undefined, { numeric: true });
    });

    return { occupiedTables: occupied, tableOrdersMap: map, prePagoOrders: prepago };
  }, [requestsDoneList]);

  if (occupiedTables.length === 0 && prePagoOrders.length === 0) {
    return null;
  }

  return (
    <>
      <div className={style.tablesDashboardSection}>
        {occupiedTables.length > 0 && (
          <div className={style.sectionBlock}>
            <h3 className={style.sectionTitle}>Pós pago Mesas</h3>
            <div className={style.tablesGrid}>
              {occupiedTables.map((table) => {
                const tableOrders = tableOrdersMap.get(table) || [];
                const { className: statusClass, label: statusLabel } = getTableStatusStyle(tableOrders);
                const isSelected = selectedTableFilter === table;
                const displayTableNum =
                  !isNaN(parseInt(table, 10)) && parseInt(table, 10) < 10
                    ? String(table).padStart(2, '0')
                    : table;

                return (
                  <button
                    type="button"
                    key={`table-btn-${table}`}
                    data-testid={`table-button-${table}`}
                    className={`${style.tableSquare} ${statusClass} ${
                      isSelected ? style.tableSelected : ''
                    }`}
                    onClick={() => onSelectTable(table)}
                    title={`Mesa ${table} - ${statusLabel} (${tableOrders.length} pedido${
                      tableOrders.length > 1 ? 's' : ''
                    })`}
                    aria-pressed={isSelected}
                  >
                    {displayTableNum}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {prePagoOrders.length > 0 && (
          <div className={style.sectionBlock}>
            <h3 className={style.sectionTitle}>Pré - Pago</h3>
            <div className={style.tablesGrid}>
              {prePagoOrders.map((order) => {
                const prepagoKey = `prepago-${order.id}`;
                const isSelected = selectedTableFilter === prepagoKey;
                const displayNum = order.countRequest
                  ? `#${order.countRequest}`
                  : `#${order.id.slice(-4)}`;

                return (
                  <button
                    type="button"
                    key={prepagoKey}
                    data-testid={`prepago-button-${order.id}`}
                    className={`${style.prePagoSquare} ${
                      isSelected ? style.tableSelected : ''
                    }`}
                    onClick={() => onSelectTable(prepagoKey)}
                    title={`Pedido Pré-Pago ${displayNum} - Cliente: ${
                      order.name || 'Cliente'
                    }`}
                    aria-pressed={isSelected}
                  >
                    {displayNum}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Barra de Filtro / Limpar Seleção */}
      {selectedTableFilter && (
        <div className={style.clearSelectionBar}>
          <span className={style.selectionInfoText}>
            Exibindo apenas:{' '}
            <strong>
              {selectedTableFilter.startsWith('prepago-')
                ? 'Pedido Pré-Pago'
                : `Mesa ${selectedTableFilter}`}
            </strong>
          </span>
          <button
            type="button"
            data-testid="clear-selection-btn"
            className={style.btnClearSelection}
            onClick={onClearFilter}
          >
            ✕ Limpar seleção (Ver todas as mesas)
          </button>
        </div>
      )}
    </>
  );
};

export default PdvTablesDashboard;

